import React, { Suspense, useState, useMemo, useEffect } from "react";
import SubTitle from "../common/SubTitle";
import SearchFilter from "../common/SearchFilter";
import SchoolDynamicTable from "../createForm/SchoolDynamicTable";
import CreateAttendantForm from "../createForm/CreateAttendant";
import axios from "axios";
import config from "../../config";
import { Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { createHasPermission } from "../../layout/navLayoutUtils";
import {
  buildFilterWidgetsFromLayout,
  buildSchoolTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
  getTableFeatures,
} from "../../layout/moduleLayoutUtils";

const legacyHeaders = [
  { key: "Names", value: "Names" },
  { key: "Staff IDs", value: "Staff IDs" },
  { key: "Phone Numbers", value: "Staff Phone No." },
  { key: "Gender", value: "Gender" },
  { key: "Routes", value: "Routes" },
  { key: "Vehicle Numbers", value: "Vehicle No." },
  { key: "actions", value: "" },
];

export default function AttendantsMain({ school = false } = {}) {
  const { t } = useTranslation();
  const { state, organizationInactive } = useUser();
  const hasPermission = useMemo(
    () =>
      createHasPermission(state?.features, {
        writesDisabled: organizationInactive,
      }),
    [state?.features, organizationInactive],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [attendantData, setAttendantData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const attendantsPageId = school ? PAGE_IDS.SCHOOL_ATTENDANTS : PAGE_IDS.FLEET_ATTENDANTS;
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? attendantsPageId : null,
  );

  const headers = useMemo(() => {
    if (moduleLayout) {
      return buildSchoolTableHeadersFromLayout(moduleLayout, { t, hasPermission }) || legacyHeaders;
    }
    return legacyHeaders;
  }, [moduleLayout, t, hasPermission]);

  const legacyFilters = useMemo(
    () => [
      {
        id: "routes",
        label: t("Routes"),
        options: [],
      },
      {
        id: "gender",
        label: t("Gender"),
        options: [
          { value: "MALE", label: t("Male") },
          { value: "FEMALE", label: t("Female") },
          { value: "OTHER", label: t("Other") },
        ],
      },
    ],
    [t],
  );

  const [routeFilterOptions, setRouteFilterOptions] = useState([]);

  const filters = useMemo(() => {
    const layoutFilters = moduleLayout ? buildFilterWidgetsFromLayout(moduleLayout) : null;
    if (!layoutFilters) {
      return legacyFilters.map((filter) =>
        filter.id === "routes" ? { ...filter, options: routeFilterOptions } : filter,
      );
    }

    return layoutFilters.map((widget) => ({
      id: widget.filterKey,
      label: widget.labelKey,
      helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
      options:
        widget.filterKey === "routes"
          ? routeFilterOptions
          : (widget.options || []).map((option) => ({
              value: option.value,
              label: option.label,
            })),
    }));
  }, [moduleLayout, legacyFilters, routeFilterOptions]);

  const tableFeatures = useMemo(() => getTableFeatures(moduleLayout) || {}, [moduleLayout]);
  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout, hasPermission) : []),
    [moduleLayout, hasPermission],
  );
  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createAttendant");
  const pageTitle = getPageTitleKey(moduleLayout) || t("Attendants");
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;
  const downloadFileName = school ? "school-attendants" : "fleet-attendants";

  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const orgId = userState.user?.organizationId;

        if (orgId) {
          setOrganizationId(orgId);
        } else {
          setAlert({
            open: true,
            message: t("Organization ID not found"),
            severity: "error",
          });
        }
      }
    } catch (err) {
      console.error("Error reading user state:", err);
      setAlert({
        open: true,
        message: t("Failed to load user information"),
        severity: "error",
      });
    }
  }, [t]);

  useEffect(() => {
    if (organizationId) {
      fetchAttendants();
      fetchRoutesForFilter();
    }
  }, [organizationId]);

  const fetchRoutesForFilter = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/route/organization?organizationId=${organizationId}`,
        { withCredentials: true },
      );

      if (response.data) {
        const routes = Array.isArray(response.data) ? response.data : [];
        const routeOptions = routes.map((route) => ({
          value: route.name,
          label: route.name,
        }));

        setRouteFilterOptions(routeOptions);
      }
    } catch (error) {
      console.error("Error fetching routes for filter:", error);
    }
  };

  const fetchAttendants = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${config.baseURL}/employee/organization`,
        {
          params: { organizationId },
          withCredentials: true,
        },
      );

      if (response.data) {
        const employees = Array.isArray(response.data) ? response.data : [];

        const transformedData = employees.map((attendant) => {
          const profilePicDoc = attendant.documents?.find(
            (doc) => doc.name === "AttendantImage",
          );

          const profilePictureUrl = profilePicDoc
            ? `${config.baseURL}/upload/image?fileName=${profilePicDoc.location}`
            : null;

          return {
            "Staff IDs": attendant.staffCode || "N/A",
            Names: (() => {
              const full =
                `${attendant.firstName || ""} ${attendant.lastName || ""}`.trim();
              return (
                (full.endsWith(" .") ? full.slice(0, -2).trim() : full) || "N/A"
              );
            })(),
            "Vehicle Numbers":
              attendant.vehicle?.VehicleNumber ||
              attendant.vehicle?.name ||
              t("Not Assigned"),
            Routes:
              attendant.vehicle?.route?.name ||
              attendant.route?.name ||
              t("Not Assigned"),
            "Phone Numbers": attendant.phoneNumber || t("N/A"),
            Gender: attendant.gender || t("N/A"),
            fullData: {
              ...attendant,
              profileImage: profilePictureUrl,
            },
          };
        });

        setAttendantData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching attendants:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t("Failed to fetch attendants"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (values) => {
    setFilterValues(values);
  };

  const handleCreateAttendant = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
  };

  const handleSubmitAttendant = async () => {
    await fetchAttendants();
    setShowCreateForm(false);
    setAlert({
      open: true,
      message: t("Attendant created successfully"),
      severity: "success",
    });
  };

  const handleUpdateAttendant = async () => {
    await fetchAttendants();
    setExpandedRow(null);
    setAlert({
      open: true,
      message: t("Attendant updated successfully"),
      severity: "success",
    });
  };

  const handleDeleteAttendant = async () => {
    if (expandedRow === null) return;

    const attendantToDelete = attendantData[expandedRow];

    try {
      await axios.delete(`${config.baseURL}/employee`, {
        data: {
          employeeId: attendantToDelete.fullData.id,
          organizationId: organizationId,
        },
        withCredentials: true,
      });

      await fetchAttendants();
      setExpandedRow(null);

      setAlert({
        open: true,
        message: t("Attendant deleted successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting attendant:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t("Failed to delete attendant"),
        severity: "error",
      });
    }
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCloseExpandedForm = () => {
    setExpandedRow(null);
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const filteredData = useMemo(() => {
    let filtered = [...attendantData];

    if (searchQuery) {
      filtered = filtered.filter((attendant) =>
        Object.entries(attendant).some(([key, value]) => {
          if (key === "fullData") return false;
          return String(value)
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        }),
      );
    }

    if (filterValues.routes) {
      filtered = filtered.filter(
        (attendant) => attendant.Routes === filterValues.routes,
      );
    }

    if (filterValues.gender) {
      filtered = filtered.filter(
        (attendant) => attendant.Gender === filterValues.gender,
      );
    }

    return filtered;
  }, [searchQuery, filterValues, attendantData]);

  const renderExpandedRow = (row) => {
    return (
      <CreateAttendantForm
        onClose={handleCloseExpandedForm}
        onSubmit={handleUpdateAttendant}
        onDelete={handleDeleteAttendant}
        initialData={row.fullData}
        isEdit={true}
      />
    );
  };

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <SubTitle
        title={pageTitle}
        path={!school ? `${orgName || "organization"} > Attendants` : undefined}
        add={!organizationInactive ? (moduleLayout ? createAction?.labelKey : t("+ Create Attendants")) : ""}
        onAddClick={!organizationInactive ? handleCreateAttendant : null}
      />
      <div className="table_container">
        {showCreateForm && !organizationInactive && (
          <CreateAttendantForm
            onClose={handleCloseForm}
            onSubmit={handleSubmitAttendant}
            isEdit={false}
          />
        )}

        {organizationInactive ? (
          <InactiveInventoryTable
            title="attendantInventory"
            headers={headers}
            data={filteredData}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="attendants"
          />
        ) : (
          <>
            <SearchFilter
              searchPlaceholder="search"
              filters={filters}
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              dataDownload={tableFeatures.downloadData !== false}
              downloadRows={filteredData}
              downloadColumns={headers
                .filter((header) => header.key !== "actions")
                .map((header) => ({
                  key: header.key,
                  label: header.value,
                }))}
              downloadFileName={downloadFileName}
            />

            <Suspense fallback={<div className="loader"></div>}>
              <SchoolDynamicTable
                headers={headers}
                data={filteredData}
                loading={isLoading}
                expandedRow={
                  tableFeatures.expandableRows === false ? null : expandedRow
                }
                onRowClick={
                  tableFeatures.expandableRows === false
                    ? undefined
                    : handleRowClick
                }
                renderExpandedRow={
                  tableFeatures.expandableRows === false
                    ? undefined
                    : renderExpandedRow
                }
                profileImageColumn={tableFeatures.profileImageColumn || "Names"}
              />
            </Suspense>
          </>
        )}
      </div>
    </>
  );
}
